<template>
  <div>
    <load-profile />
    <!-- Search Container Card -->
    <n-search-container
      :fields="searchFields"
      :default-visible="true"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      >
        <template #startDate>
          <div v-if="params.period != 'other'" />
        </template>
        <template #endDate>
          <div v-if="params.period != 'other'" />
        </template>
      </n-search-input>
    </n-search-container>

    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-2">
        <b-card-title class="mb-1">
          {{ $t('general.detailReport') }}
        </b-card-title>
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t('selection.noOption') }}
              </template>
            </v-select>
          </b-col>
          <b-col class="text-right">
            <b-button
              variant="success"
              @click="exportExcel"
            >
              {{ $t('button.exportExcel') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :type="permissionType"
          :previewable="false"
          @sort-changed="sortChanged"
        >
          <template #cell(avgTimeToResolve)="data">
            {{ formatResolveTime(data.item.avgTimeToResolve) || '-' }}
          </template>
          <template #cell(cumulativeTimeToResolve)="data">
            {{ formatResolveTime(data.item.cumulativeTimeToResolve) || '-' }}
          </template>
          <template #cell(restTime)="data">
            {{ formatRestTime(data.item.restTime) || '-' }}
          </template>
          <template #cell(reworkDetail)="data">
            {{ formatRework(data.item.reworkDetail) || '-' }}
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BAvatar,
  BButton,
  // BCardHeader,
  BCardTitle,
} from 'bootstrap-vue'
import NSearchContainer from '@/components/NSearchContainer.vue'
import NSearchInput from '@/components/NSearchInput.vue'
import NPagination from '@/components/NPagination.vue'
import NTable from '@/components/NTable.vue'
import vSelect from 'vue-select'
import { avatarText } from '@core/utils/filter'
import Repository from '@/repositories/RepositoryFactory'
import { calculateDatesByPeriod } from '@/libs/helper'
import moment from 'moment-timezone'
import TableFields from './tableFields'
import SearchInputs from './searchInput'

const TeamPerformanceRepository = Repository.get(
  'employeePerformanceOverview',
)

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BAvatar,
    BButton,
    // BCardHeader,
    BCardTitle,
    NSearchContainer,
    NSearchInput,
    NPagination,
    NTable,
    vSelect,
  },
  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        period: this.$route.query.period || 'thisWeek',
        operator: this.$route.query.operator || null,
        startDate: this.$route.query.startDate || null,
        endDate: this.$route.query.endDate || null,
        currentDate: new Date().toISOString().split('T')[0],
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        period: this.$route.query.period || 'thisWeek',
        operator: this.$route.query.operator || null,
        startDate: this.$route.query.startDate || null,
        endDate: this.$route.query.endDate || null,
      },
      moment,
      avatarText,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
      currentLocale() {
        return this.$i18n.locale
      },
    },
  },
  watch: {
    perPage() {
      this.list()
    },
    'params.period': function periodWatcher() {
      this.calculateDate()
    },
  },
  mounted() {
    this.getData()
  },
  created() {
    this.a = document.createElement('a')
    document.body.appendChild(this.a)
    this.a.style = 'display: none'
  },
  methods: {
    formatRestTime(minutes) {
      const ms = minutes && typeof minutes === 'number' ? minutes * 60 * 1000 : 0
      return this.formatResolveTime(ms)
    },
    formatRework(reworkDetail) {
      return reworkDetail?.totalReworkCount || 0
    },
    formatResolveTime(ms) {
      const seconds = Math.floor(ms / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)

      const remainingHours = hours % 24
      const remainingMinutes = minutes % 60
      const remainingSeconds = seconds % 60

      let result = ''

      if (days > 0) {
        result += `${days} ${this.$tc('time.day', days)} `
      }
      if (remainingHours > 0) {
        result += `${remainingHours} ${this.$tc('time.hour_short', remainingHours)} `
      }
      if (remainingMinutes > 0) {
        result += `${remainingMinutes} ${this.$tc('time.minute_short', remainingMinutes)} `
      }
      if (remainingSeconds > 0 || result === '') {
        result += `${remainingSeconds} ${this.$tc('time.second_short', remainingSeconds)}`
      }

      return result.trim()
    },
    toMinutesSeconds(totalSeconds) {
      const seconds = Math.floor(totalSeconds % 60)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600)
      const secondsStr = this.makeHumanReadable(seconds, 'second')
      const minutesStr = this.makeHumanReadable(minutes, 'minute')
      const hoursStr = this.makeHumanReadable(hours, 'hour')
      const string = `${hoursStr}${minutesStr}${secondsStr}`.replace(
        /,\s*$/,
        '',
      )

      return string || `0 ${this.$tc('time.second', 0)}`
    },
    makeHumanReadable(num, singular) {
      return num > 0 ? `${num} ${this.$tc(`time.${singular}`, num)} ` : ''
    },
    calculateDate() {
      let dates
      if (this.params.period === 'other') {
        dates = calculateDatesByPeriod('thisWeek')
      } else {
        dates = calculateDatesByPeriod(this.params.period)
      }
      if (dates.length) {
        [this.params.startDate, this.params.endDate] = dates
      }
    },
    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQuerySting()
      this.getData()
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.params.period = 'thisWeek'
      this.calculateDate()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    getData() {
      this.loading = true
      if (!this.params.startDate || !this.params.endDate) {
        this.calculateDate()
        this.query = {
          ...this.query,
          ...this.params,
        }
      }
      TeamPerformanceRepository.index({
        ...this.query,
      })
        .then(response => {
          const data = response?.data?.data
          if (data) {
            this.items = [...data.list]
            this.total = data.total
          }
        })
        .catch(() => {
          this.loading = false
        })
        .then(() => {
          this.loading = false
        })
    },
    exportExcel() {
      this.loading = true
      TeamPerformanceRepository.exportExcel({
        ...this.query,
        lang: this.$i18n.locale.split('-')[0] === 'zh' ? 'zh' : 'en',
      })
        .then(response => {
          const utf8WithBom = `\uFEFF${response.data}` // Add BOM for UTF-8
          const blob = new Blob([utf8WithBom], {
            type: 'text/csv;charset=utf-8;',
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url

          let fn = 'employee_performance_report'
          if (this.$i18n.locale.split('-')[0] === 'zh') {
            fn = '员工绩效表'
          }

          const fileName = `${fn}_${this.params.startDate
            .split('T')[0]
            .replace(/[^a-z0-9]/gi, '_')}-${this.params.endDate
            .split('T')[0]
            .replace(/[^a-z0-9]/gi, '_')}.csv`

          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          // Clean up
          URL.revokeObjectURL(url)
        })
        .catch(() => {
          this.loading = false
        })
        .then(() => {
          this.loading = false
        })
    },
  },
  setup() {
    const fields = TableFields
    const searchFields = SearchInputs
    const permissionType = 'employee-performance'
    return {
      fields,
      searchFields,
      permissionType,
    }
  },
}
</script>
